"use client";

import { useAuth, usePermissions } from "@/hooks/use-auth";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Package,
  Users,
  Shield,
  FileSearch,
  Settings,
  BarChart3,
} from "lucide-react";
import Link from "next/link";
import ForcedPasswordResetModal from "@/components/auth/forced-password-reset-modal";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useSetPageHeader } from "@/hooks/use-page-header";

/**
 * Dashboard Page
 *
 * Main landing page for authenticated users
 * Shows welcome message and quick links to key features based on permissions
 */
export default function DashboardPage() {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const {
    permissions,
    isLoading: permissionsLoading,
    hasPermission,
  } = usePermissions();
  const router = useRouter();
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Set page header
  useSetPageHeader({
    title: `Welcome back, ${user?.firstName || "User"}!`,
    subtitle: "Here's an overview of your Omizi Inventory Management System"
  });

  // Check for password reset requirement on component mount and when user changes
  useEffect(() => {
    if (!authLoading && user) {
      const needsPasswordReset =
        (user.publicMetadata?.password_reset_required as boolean) ?? false;
      setShowPasswordReset(needsPasswordReset);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.push("/sign-in");
    }
  }, [authLoading, isAuthenticated, router]);

  if (authLoading || permissionsLoading) {
    return <LoadingScreen message="Loading dashboard..." />;
  }

  if (!isAuthenticated) {
    return null;
  }

  // Handle password reset completion
  const handlePasswordResetComplete = () => {
    setShowPasswordReset(false);
    // Refresh user data to confirm the flag was cleared
    // The useAuth hook should automatically update when the user record changes
  };

  // Quick links based on user permissions
  const quickLinks = [];

  if (hasPermission("org:inventory:read")) {
    quickLinks.push({
      title: "Inventory",
      description: "Manage inventory items and stock levels",
      icon: Package,
      href: "/inventory",
      color: "text-blue-500",
    });
  }

  if (hasPermission("org:reports:read")) {
    quickLinks.push({
      title: "Reports",
      description: "View analytics and business reports",
      icon: BarChart3,
      href: "/reports",
      color: "text-green-500",
    });
  }

  if (hasPermission("org:system:manage")) {
    quickLinks.push({
      title: "Admin",
      description: "Manage users, roles, and system settings",
      icon: Shield,
      href: "/admin/users",
      color: "text-purple-500",
    });
  }

  // Always show settings
  quickLinks.push({
    title: "Settings",
    description: "Update your profile and preferences",
    icon: Settings,
    href: "/profile",
    color: "text-muted-foreground",
  });

  return (
    <>
      {/* Forced Password Reset Modal */}
      <ForcedPasswordResetModal
        open={showPasswordReset}
        onResetComplete={handlePasswordResetComplete}
      />

      <div className="p-6 space-y-8">

        {/* Quick Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Permissions
              </CardTitle>
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground">
                {permissions.length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">permissions granted</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Account Type
              </CardTitle>
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-secondary/10">
                <Users className="h-5 w-5 text-secondary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground capitalize">
                {(user?.publicMetadata?.user_type as string) || "User"}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {user?.emailAddresses?.[0]?.emailAddress}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                System Status
              </CardTitle>
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-accent/10">
                <Package className="h-5 w-5 text-accent" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-green-500">
                Active
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                All systems operational
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Quick Actions
              </CardTitle>
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-primary/10">
                <FileSearch className="h-5 w-5 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-foreground">
                {quickLinks.length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">available shortcuts</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">Quick Access</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.href} href={link.href}>
                  <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <CardTitle>{link.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-muted-foreground">
                        {link.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Getting Started Section */}
        <Card>
          <CardHeader>
            <CardTitle>Getting Started</CardTitle>
            <CardDescription className="text-muted-foreground">
              New to Omizi IMS? Here are some resources to help you get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">For Administrators:</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Set up user accounts and assign roles</li>
                <li>Configure permissions for your team</li>
                <li>Review audit logs for security monitoring</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">For Users:</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Update your profile in Settings</li>
                <li>Explore available features in the navigation menu</li>
                <li>Contact support if you need assistance</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
